import { inviteCreateSchema, type MembershipRole, ROLES_BY_ORG_KIND } from '@fruuit/shared'
import { Button, Dialog, DialogCancel, Input, Select, useToast } from '@fruuit/ui'
import { Send } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { errorMessage } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { ROLE_LABELS } from '../auth/format'
import { useStepUp } from '../auth/StepUp'
import { adminApi, adminKeys, useAdminOrgs } from './api'

export interface InviteDialogProps {
  open: boolean
  onClose: () => void
  /** Preselect (and with `lockOrg`, fix) the organisation. */
  orgId?: string
  lockOrg?: boolean
  title?: string
}

type FieldErrors = Partial<Record<'email' | 'org_id' | 'role' | 'display_name', string>>

/** Invite a person to an org with a role (P-ID-010, P-ADM-007/008). */
export function InviteDialog({ open, onClose, orgId, lockOrg = false, title }: InviteDialogProps) {
  const orgs = useAdminOrgs({ limit: 200, status: 'active' })
  const { toast } = useToast()
  const { guard, dialog } = useStepUp()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [org, setOrg] = useState(orgId ?? '')
  const [role, setRole] = useState<MembershipRole | ''>('')
  const [errors, setErrors] = useState<FieldErrors>({})

  useEffect(() => {
    if (open) {
      setEmail('')
      setName('')
      setOrg(orgId ?? '')
      setRole('')
      setErrors({})
    }
  }, [open, orgId])

  const selected = orgs.data?.items.find((o) => o.id === org)
  const roles = selected ? ROLES_BY_ORG_KIND[selected.kind] : []
  useEffect(() => {
    if (selected && role && !roles.includes(role)) setRole('')
  }, [selected, role, roles])

  const send = useApiMutation(
    (vars: Parameters<typeof adminApi.invite>[0], ctx) => guard(() => adminApi.invite(vars, ctx)),
    {
      invalidate: [adminKeys.users, adminKeys.orgs, adminKeys.staff, adminKeys.invites],
      onSuccess: (result) => {
        toast({
          tone: 'success',
          message:
            result.outcome === 'granted'
              ? `${result.invite.email} now has access to ${result.invite.org_name}.`
              : result.email_sent
                ? `Invitation sent to ${result.invite.email}.`
                : `Invitation created for ${result.invite.email}. Email is not configured, so no message was sent.`,
        })
        onClose()
      },
    },
  )

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const parsed = inviteCreateSchema.safeParse({
      email,
      org_id: org,
      role: role || undefined,
      ...(name.trim() ? { display_name: name } : {}),
    })
    if (!parsed.success) {
      const next: FieldErrors = {}
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]) as keyof FieldErrors
        next[key] ??=
          key === 'role'
            ? 'Choose a role.'
            : key === 'org_id'
              ? 'Choose an organisation.'
              : issue.message
      }
      setErrors(next)
      return
    }
    setErrors({})
    send.mutate(parsed.data)
  }

  const serverErrors =
    send.error && 'fieldErrors' in send.error ? (send.error.fieldErrors as FieldErrors) : {}

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={title ?? 'Invite a person'}
        description="They get an email with a single-use link that works for 7 days. Existing active users get the role straight away."
        footer={
          <>
            <DialogCancel />
            <Button type="submit" form="invite-form" icon={<Send />} loading={send.isPending}>
              Send invitation
            </Button>
          </>
        }
      >
        <form id="invite-form" onSubmit={onSubmit} className="grid gap-4" noValidate>
          <Input
            label="Work email"
            type="email"
            name="email"
            required
            autoComplete="off"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={errors.email ?? serverErrors.email}
          />
          <Input
            label="Name"
            hint="Optional. Taken from the email address if left empty."
            name="display_name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={errors.display_name ?? serverErrors.display_name}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Organisation"
              name="org_id"
              required
              disabled={lockOrg}
              value={org}
              onChange={(event) => setOrg(event.target.value)}
              placeholder={orgs.isPending ? 'Loading…' : 'Choose an organisation'}
              options={(orgs.data?.items ?? []).map((o) => ({ value: o.id, label: o.name }))}
              error={errors.org_id ?? serverErrors.org_id}
            />
            <Select
              label="Role"
              name="role"
              required
              disabled={!selected}
              value={role}
              onChange={(event) => setRole(event.target.value as MembershipRole)}
              placeholder="Choose a role"
              options={roles.map((r) => ({ value: r, label: ROLE_LABELS[r] ?? r }))}
              error={errors.role ?? serverErrors.role}
            />
          </div>
          {send.error && Object.keys(serverErrors).length === 0 ? (
            <p role="alert" className="text-sm text-danger">
              {errorMessage(send.error)}
            </p>
          ) : null}
        </form>
      </Dialog>
      {dialog}
    </>
  )
}
